import type { BrowserBroadcast } from '@adapters/broadcast/browserBroadcast';
import { RedirectToExternalBankPage } from '@adapters/navigation/page';
import { Component, Inject, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import type { Logger } from '@core/model/domain/logger';
import { Option } from '@core/model/framework/Result/Option';
import { RegionalBankPayload } from '@core/service/digital-factory/bank/types/dto/connectionFormDto';
import { GetWebauthUrl } from '@core/usecase/getWebauthUrl';
import { TrackerService } from '@shared/services/tracker.service';
import { firstValueFrom } from 'rxjs';
import { browserBroadcastToken, loggerToken } from 'src/conf/di/angularProviderMapper';
import { Buffer } from 'buffer';
import { RedirectToExternalBankErrorComponent } from './pages/redirect-to-external-bank-error/redirect-to-external-bank-error.component';
import { RedirectToExternalBankWaitComponent } from './pages/redirect-to-external-bank-wait/redirect-to-external-bank-wait.component';
import { InitInput, RedirectToExternalBankVM } from './redirect-to-external-bank.viewmodel';

export type RedirectExternalPageSelectedBank = {
  name: string;
  logo: Option<string>;
  uid: string;
};

@Component({
  selector: 'app-redirect-to-external-bank',
  templateUrl: './redirect-to-external-bank.component.html',
  standalone: true,
  imports: [RedirectToExternalBankWaitComponent, RedirectToExternalBankErrorComponent],
})
export class RedirectToExternalBankComponent implements OnInit {
  selectedBank: RedirectExternalPageSelectedBank | undefined;
  private redirectToExternalBankVM: RedirectToExternalBankVM;

  constructor(
    private activatedRoute: ActivatedRoute,
    private getWebauthUrl: GetWebauthUrl,
    private tracker: TrackerService,
    @Inject(browserBroadcastToken) private broadcastService: BrowserBroadcast,
    @Inject(loggerToken) private loggerService: Logger
  ) {
    this.redirectToExternalBankVM = new RedirectToExternalBankVM(
      this.getWebauthUrl,
      this.broadcastService,
      this.loggerService
    );
  }

  async ngOnInit(): Promise<void> {
    const { tagging, selectedBank, regionalBank, userToken } = (await firstValueFrom(
      this.activatedRoute.queryParams
    )) as Record<string, string>;

    if (!selectedBank) {
      throw new Error('RedirectToExternalBankComponent: selectedBank query param should be provided');
    }
    if (!regionalBank) {
      throw new Error('RedirectToExternalBankComponent: webauthForm query param should be provided');
    }
    if (!userToken) {
      throw new Error('RedirectToExternalBankComponent: userToken query param should be provided');
    }

    const regionalBankParsed = JSON.parse(
      Buffer.from(regionalBank, 'base64').toString()
    ) as Option<RegionalBankPayload>;

    this.selectedBank = JSON.parse(
      Buffer.from(selectedBank, 'base64').toString()
    ) as RedirectExternalPageSelectedBank;

    const mappedQueryParams: InitInput = {
      tagging: tagging,
      userToken: userToken,
      selectedBankUid: this.selectedBank.uid,
      regionalBank: regionalBankParsed,
    };

    if (tagging != null) {
      await this.tracker.initializeTaggingTracker(tagging);
      this.tracker.trackPage({ taggingPage: 'REDIRECT_TO_EXTERNAL_BANK_WAIT' });
    } else {
      this.loggerService.logTrace('REDIRECT_TO_EXTERNAL_BANK: No tagging query param');
    }
    await this.redirectToExternalBankVM.onInit(mappedQueryParams);
  }

  get redirectToExternalBankPage(): RedirectToExternalBankPage {
    return this.redirectToExternalBankVM.redirectToExternalBankPage;
  }
}
